import { Dataset } from '@api/customTypes';
import { DeckardFilter } from '@components/filter';
import { GraphqlPagination, useGraphqlPagination } from '@components/infiniteScroll';
import {
  DatasetFilter,
  DatasetSearchDetail,
  GetDatasetsWithFilterQuery,
  useGetDatasetsSearchDetailQuery,
  useGetDatasetsWithFilterQuery,
} from '@generated/graphql';
import { useSearchDetails } from '@hooks/SearchDetails';
import { useQueryParam } from '@hooks/UseQueryParam';
import {
  Dispatch,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type DatasetsProviderType = {
  datasets: Dataset[];
  filter: DeckardFilter<DatasetFilter>;
  activeFilters: boolean;
  loading: boolean;
  pagination: GraphqlPagination<Dataset>;
  searchDetail: DatasetSearchDetail;
  selectedDataset: string;
  selectedDatasets: string[];
  refetchDatasets: VoidFunction;
  setFilter: Dispatch<DeckardFilter<DatasetFilter>>;
  setSelectedDataset: Dispatch<string>;
  setSelectedDatasets: Dispatch<string[]>;
};

interface DatasetsProviderProps {
  workspaceId: string;
}

const DatasetsContext = createContext<DatasetsProviderType>({} as DatasetsProviderType);
export const DatasetsProvider = ({
  children,
  workspaceId,
}: PropsWithChildren<DatasetsProviderProps>) => {
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);

  const { value: selectedDataset, setValue: setSelectedDataset } = useQueryParam('datasetId', '');

  const { activeFilters, filter, queryFilter, setFilter } = useSearchDetails<DatasetFilter>({
    channels: { type: 'exact', value: [], display: [] },
    dates: { type: 'date', value: [], display: [] },
    graphs: { type: 'text', value: [], display: [] },
    runs: { type: 'number', value: [], display: [] },
    size: { type: 'size', value: [], display: [] },
    tags: { type: 'string', value: [], display: [] },
    texts: { type: 'string', value: [], display: [] },
    type: { type: 'exact', value: [], display: [] },
    users: { type: 'exact', value: [], display: [] },
  });

  const { data, refetch: refetchSearchDetail } = useGetDatasetsSearchDetailQuery({
    variables: { workspaceId },
  });
  const searchDetail = data?.getDatasetsSearchDetail;

  const pagination = useGraphqlPagination<GetDatasetsWithFilterQuery, Dataset>(
    useGetDatasetsWithFilterQuery({
      variables: {
        workspaceId,
        limit: 50,
        filters: queryFilter,
      },
    }),
    'getDatasets',
    'datasetId',
    { pollInterval: 5000 },
  );

  const datasets = pagination.data;

  const refetch = useCallback(() => {
    void pagination.refetch();
    void refetchSearchDetail();
  }, [pagination, refetchSearchDetail]);

  const value = useMemo(
    () => ({
      datasets,
      filter,
      activeFilters,
      loading: pagination.loading,
      pagination,
      searchDetail,
      selectedDataset,
      selectedDatasets,
      refetchDatasets: refetch,
      setFilter,
      setSelectedDataset,
      setSelectedDatasets,
    }),
    [
      datasets,
      filter,
      activeFilters,
      pagination,
      searchDetail,
      selectedDataset,
      selectedDatasets,
      refetch,
      setFilter,
      setSelectedDataset,
      setSelectedDatasets,
    ],
  );

  return <DatasetsContext.Provider value={value}>{children}</DatasetsContext.Provider>;
};

export const useDatasets = () => useContext(DatasetsContext);
export const useDataset = (datasetId: string) => {
  const { datasets } = useDatasets();
  return datasets.find((dataset) => dataset.datasetId === datasetId);
};
export const useSelectedDataset = () => {
  const { datasets, selectedDataset } = useDatasets();
  return datasets.find((dataset) => dataset.datasetId === selectedDataset);
};
